import { useGLTF, useTexture } from '@react-three/drei'
import { useSnapshot } from 'valtio'
import { state } from '../../context/store'
import {
  modelParts,
  numberCoords,
  badgeCoords,
  leftLogoParts,
  rightLogoParts,
  leftLogoCoords,
  rightLogoCoords,
} from '../../assets/models/174'
import {
  Ribs,
  MainSeat,
  RaceNumber,
  LogoMain,
  LogoRight,
  LogoLeft,
  TopPanelTexture,
  SuperBadge
} from './ModelSubComponents'
import { useRef } from 'react'
import { logoImagesLeft, logoImagesRight } from '../textures'
import * as THREE from 'three'
import { degsToRads } from '../../utilities'

// This is the main motorcycle seat
// Link to test
// /?type=motorcycle&subtype=ribbed&test=yes

const Model174 = (props) => {
  const snap = useSnapshot(state.current)
  const { nodes, materials } = useGLTF(modelParts.main)

  // Model Variables
  const masterScale = 1.2
  const neededRibs = 7
  const ribPosition = [0.002, 0.025, 0.285]
  const ribScale = 1.063
  const topPanelTextureRepeat = 3
  const enjoyLogoPosition = [0.002, 0.025, 0.285]
  const enjoyLogoScale = 1.063
  const hexaLogoPosition = [0.003, 0.024, 0.284]
  const hexaLogoScale = 0.817
  const superLogoPosition = [0.002, 0.022, 0.285]
  const superLogoScale = 1.08

  //________ No edits required below ________//

  // Controls which extra parts are visible
  const visibleRibs = snap.choices.ribs.selected.mode !== 'no'


  // const visibleSuperBadge = snap.choices.superBadge.selected.mode !== 'none'

  const visibleSuperBadge =
    snap.choices.superBadge?.selected?.mode !== 'none' &&
    !!snap.choices.superBadge?.selected;

  const visibleTopLogo = snap.initialChoices.logoSize !== 'none'
  const visibleNumber = snap.choices.raceNumber.selected.mode === 'number'
  const visibleLogos = snap.choices.sideLogos.mode === 'true'
  const visiblePattern = snap.choices.topPanel.selected.src !== null

  const FlatLogoLeft = () => {
    const { nodes, materials } = useGLTF(modelParts.logoLeft)

    const logoRef = useRef()
    let logoTexture = useTexture(
      logoImagesLeft[snap.choices.sideLogos.selected.mode] ?? logoImagesLeft.none
    )
    // console.log(state.current.choices.sideLogos.selected.mode)
    logoTexture.repeat.set(1, -1)
    logoTexture.wrapS = THREE.RepeatWrapping
    logoTexture.wrapT = THREE.RepeatWrapping
    logoTexture.flipY = true
    logoTexture.needsUpdate = true

    return (
      <group {...props} dispose={null}>
        <mesh
          castShadow={false}
          receiveShadow={false}
          transparent
          depthWrite={false}
          depthTest={false}
          geometry={nodes.LogoLeft.geometry}
          material={materials.LogosSide}
        >
          {snap.choices.sideLogos.selected !== 'none' && (
            <meshStandardMaterial
              ref={logoRef}
              map={logoTexture}
              alphaTest={0.5}
              side={THREE.DoubleSide}
              onUpdate={(self) => (self.needsUpdate = true)}
            />
          )}
        </mesh>
      </group>
    )
  }
  const FlatLogoRight = () => {
    const { nodes, materials } = useGLTF(modelParts.logoRight)

    const logoRef = useRef()
    // const imageRef = logoImagesRight[snap.sideLogoMode]
    // const imageLink = imageRef.slice(0, -5) + "-right" + imageRef.slice(-5)

    let logoTextureRight = useTexture(
      logoImagesRight[snap.choices.sideLogos.selected.mode] ?? logoImagesRight.none
    )
    logoTextureRight.repeat.set(1, -1)
    logoTextureRight.wrapS = THREE.RepeatWrapping
    logoTextureRight.wrapT = THREE.RepeatWrapping
    logoTextureRight.flipY = true
    logoTextureRight.needsUpdate = true

    return (
      <group {...props} dispose={null}>
        <mesh
          castShadow={false}
          receiveShadow={false}
          depthWrite={false}
          depthTest={false}
          geometry={nodes.LogoRight.geometry}
        >
          {snap.choices.sideLogos.selected !== 'none' && (
            <meshStandardMaterial
              ref={logoRef}
              map={logoTextureRight}
              alphaTest={0.5}
              side={THREE.DoubleSide}
              onUpdate={(self) => (self.needsUpdate = true)}
            />
          )}
        </mesh>
      </group>
    )
  }

  return (
    <group {...props} dispose={null} scale={masterScale}>
      <MainSeat nodes={nodes} materials={materials}>
        {visiblePattern && (
          <TopPanelTexture
            repeat={topPanelTextureRepeat}
            normal={materials.TopPanel.normalMap}
          />
        )}
      </MainSeat>

      {visibleRibs && (
        <Ribs
          model={modelParts.ribs}
          neededRibs={neededRibs}
          position={ribPosition}
          scale={ribScale}
          tiles={0.7}
        />
      )}
      {visibleTopLogo && !visibleSuperBadge && (
        <LogoMain
          model={modelParts.logoMain}
          model2={modelParts.logoHexa}
          model3={modelParts.logoSuper}
          position={enjoyLogoPosition}
          scale={enjoyLogoScale}
          position2={hexaLogoPosition}
          scale2={hexaLogoScale}
          position3={superLogoPosition}
          scale3={superLogoScale}
        />
      )}

      <RaceNumber model={modelParts.number} coords={numberCoords} />

      {visibleSuperBadge && (

        <SuperBadge model={modelParts.badge} {...badgeCoords} mode={snap.choices.superBadge.selected.subMode} />
      )}



      <FlatLogoLeft />
      <FlatLogoRight />


      <LogoLeft
        model={leftLogoParts}
        logo={snap.choices.sideLogos.selected.mode}
        coords={leftLogoCoords}
      />
      <LogoRight
        model={rightLogoParts}
        logo={snap.choices.sideLogos.selected.mode}
        coords={rightLogoCoords}
      />

      {/* {visibleLogos && <LogoRight model={modelParts.logoRight} logo={snap.choices.sideLogos.selected} position={[0, 0.014, 0.286]} rotation={[0, 1, 0]} scale={0.792} />}
            {visibleLogos && <LogoLeft model={modelParts.logoLeft} logo={snap.choices.sideLogos.selected} position={[0, 0.014, 0.286]} rotation={[0, 0, 0]} scale={0.792} />}
            {visibleNumber && <RaceNumber model={modelParts.raceNum} position={[[0, 0.099, -0.328]]} rotation={[-1.2, 0, 0]} scale={[.03, .03, .03]} />} */}
    </group>
  )
}

export default Model174

useGLTF.preload(modelParts.main)
useGLTF.preload(modelParts.logoLeft)
useGLTF.preload(modelParts.logoRight)
useGLTF.preload(modelParts.logoMain)
